import { Dispatch, FC, SetStateAction, useState } from 'react';
import { IInfrastructureProjectObject } from '../../../types/project.types';
import generalClasses from '../tabs/styles.module.scss';
import { PlusIcon, XIcon } from '../../../assets/icons';
import Field from '../../../components/field/field.component';
import { t } from '../../../helpers/utils';
import classes from './styles.module.scss';

const translationKey = 'projects.infrastructure-projects';

interface IProps {
  data: IInfrastructureProjectObject;
  setData: Dispatch<SetStateAction<IInfrastructureProjectObject>>;
  lang: 'ru' | 'kz';
  newText: {
    textRu: string;
    textKz: string;
  };
  setNewText: Dispatch<SetStateAction<{ textRu: string; textKz: string }>>;
  newInfo: { labelRu: string; labelKz: string; valueRu: string; valueKz: string };
  setNewInfo: Dispatch<SetStateAction<{ labelRu: string; labelKz: string; valueRu: string; valueKz: string }>>;
  isAddingText: boolean;
  setIsAddingText: Dispatch<SetStateAction<boolean>>;
  isAddingInfo: boolean;
  setIsAddingInfo: Dispatch<SetStateAction<boolean>>;
  parentErrors: Record<string, boolean>;
}

const AdditionalInfo: FC<IProps> = ({
  data,
  setData,
  lang,
  newText,
  setNewText,
  newInfo,
  setNewInfo,
  isAddingText,
  setIsAddingText,
  isAddingInfo,
  setIsAddingInfo,
  parentErrors
}) => {
  const [errors, setErrors] = useState<{ label?: boolean; value?: boolean; text?: boolean }>({});
  const suffix = lang === 'kz' ? 'Kz' : 'Ru';

  const handleAddInfo = () => {
    const { labelRu, labelKz, valueRu, valueKz } = newInfo;
    const newErrors: typeof errors = {};
    if (!labelRu || !labelKz) newErrors.label = true;
    if (!valueRu || !valueKz) newErrors.value = true;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const key = `info-${Date.now()}`;
    setData(prev => ({
      ...prev,
      infrastructureEntity: {
        ...prev.infrastructureEntity,
        additionalFields: {
          ...prev.infrastructureEntity.additionalFields,
          [key]: { labelRu, labelKz, valueRu, valueKz }
        }
      }
    }));

    setNewInfo({ labelRu: '', labelKz: '', valueRu: '', valueKz: '' });
    setIsAddingInfo(false);
    setErrors({});
  };

  const handleAddText = () => {
    if (!newText.textRu || !newText.textKz) {
      setErrors({ text: true });
      return;
    }

    const key = `text-${Date.now()}`;
    setData(prev => ({
      ...prev,
      infrastructureEntity: {
        ...prev.infrastructureEntity,
        additionalFields: {
          ...prev.infrastructureEntity.additionalFields,
          [key]: { textRu: newText.textRu, textKz: newText.textKz }
        }
      }
    }));

    setNewText({ textRu: '', textKz: '' });
    setIsAddingText(false);
    setErrors({});
  };

  const handleDelete = (key: string) => {
    setData(prev => {
      const updated = { ...prev.infrastructureEntity.additionalFields };
      delete updated[key];
      return {
        ...prev,
        infrastructureEntity: {
          ...prev.infrastructureEntity,
          additionalFields: updated
        }
      };
    });
  };

  return (
    <div className={`${generalClasses['field-block']} ${generalClasses['field-block--gray']}`}>
      <div className={generalClasses['field-block__title']}>{t(`${translationKey}.additional-info`, lang)}</div>

      {Object.entries(data.infrastructureEntity.additionalFields || {}).map(([key, item]) => {
        const value = key.includes('text') ? item[`text${suffix}`] : item[`value${suffix}`];
        const valueKey = key.includes('text') ? `text${suffix}` : `value${suffix}`;
        return (
          <div className={classes[`sport-sections__item`]} key={key}>
            <div className={generalClasses['field-block__label']} style={{ marginBottom: '0.625rem' }}>
              {item[`label${suffix}`]}
            </div>
            <Field
              label={""}
              value={value || ''}
              onChange={(val) =>
                setData(prev => ({
                  ...prev,
                  infrastructureEntity: {
                    ...prev.infrastructureEntity,
                    additionalFields: {
                      ...prev.infrastructureEntity.additionalFields,
                      [key]: { ...item, [valueKey]: val.toString() }
                    }
                  }
                }))
              }
              type="text"
              as={key.includes('text') ? 'textarea' : 'input'}
              rows={key.includes('text') ? 3 : undefined}
              fieldType="row"
              required
              error={parentErrors[`additionalFields.${key}.${valueKey}`]}
            />
            <button className={classes[`sport-sections__btn`]} onClick={() => handleDelete(key)}><XIcon /></button>
          </div>
        )
      })}

      {!isAddingInfo && !isAddingText && (
        <div className={generalClasses['field-block__actions']}>
          <button onClick={() => setIsAddingInfo(true)}><PlusIcon /> {t('actions.add', lang)}</button>
          <button onClick={() => setIsAddingText(true)}><PlusIcon /> {t('actions.add-info', lang)}</button>
        </div>
      )}

      {isAddingInfo && (
        <div className={generalClasses['field-block']}>
          <Field
            key={`label${suffix}`}
            label={t(`${translationKey}.label`, lang)}
            value={newInfo[`label${suffix}`]}
            onChange={(value) => setNewInfo(prev => ({ ...prev, [`label${suffix}`]: value }))}
            type="text"
            required
            error={errors.label}
          />
          <Field
            key={`value${suffix}`}
            label={t(`${translationKey}.value`, lang)}
            value={newInfo[`value${suffix}`]}
            onChange={(value) => setNewInfo(prev => ({ ...prev, [`value${suffix}`]: value }))}
            type="text"
            required
            error={errors.value}
          />
          <div className={generalClasses['field-block__actions']}>
            <button onClick={handleAddInfo}>{t(`actions.save`, lang)}</button>
            <button onClick={() => { setIsAddingInfo(false); setErrors({}); }}>{t(`actions.cancel`, lang)}</button>
          </div>
        </div>
      )}

      {isAddingText && (
        <div className={generalClasses['field-block']}>
          <Field
            key={`text${suffix}`}
            label={t(`${translationKey}.additional-info.text`, lang)}
            value={newText[`text${suffix}`]}
            onChange={(val) => setNewText((prev) => ({ ...prev, [`text${suffix}`]: val }))}
            as="textarea"
            required
            error={errors.text}
            rows={3}
          />
          <div className={generalClasses['field-block__actions']}>
            <button onClick={handleAddText}>{t(`actions.save`, lang)}</button>
            <button onClick={() => { setIsAddingText(false); setErrors({}); }}>{t(`actions.cancel`, lang)}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalInfo;
